import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TextInput from 'components/inputs/TextInput';
import CustomSelect from 'components/inputs/CustomSelect';
import SubmitButton from 'components/SubmitButton';
import {Service} from 'types/service';
import {ServicesThunks} from 'features/programs/program/services/servicesThunks';
import {setIsEditing} from 'features/programs/program/services/servicesSlice';
import XIcon from 'assets/images/icons/XIcon';
import DescriptionInput from 'components/inputs/DescriptionInput';
import RadioInput from 'components/inputs/RadioInput';
import {Credential} from 'types/credential';
import {PlaceOfService} from 'types/placeOfService';
import {useParams} from 'react-router-dom';
import {CredentialService} from 'services/credentialService';
import {PlaceOfServiceService} from 'services/placeOfServiceService';
import {ServiceCredentialModifier} from 'types/serviceCredentialModifier';

interface ServicesDrawerProps {
  id?: string;
  closeDrawer: () => void;
}

const ServiceDrawer: React.FC<ServicesDrawerProps> = ({closeDrawer}) => {
  const service = useSelector((state: RootState) => state.service.service);
  const dispatch = useDispatch<AppDispatch>();
  const [updatedService, setUpdatedService] = useState({...service});
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [placesOfServiceOptions, setPlacesOfServiceOptions] = useState<
    {value: string; label: string}[]
  >([]);
  const [defaultPlaceOfServiceOptions, setDefaultPlaceOfServiceOptions] =
    useState<{value: string; label: string}[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [placeOfServices, setPlaceOfServices] = useState<PlaceOfService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const params = useParams();

  const applicableStateOptions = [
    {value: 'assessment', label: 'Assessment'},
    {value: 'treatment', label: 'Treatment'},
  ];

  const addNewCredential = () => {
    setUpdatedService({
      ...updatedService,
      serviceCredentialModifiers: [
        ...(updatedService.serviceCredentialModifiers || []),
        new ServiceCredentialModifier({
          modifier: '',
          credentialId: '',
          id: `element-${Date.now()}`,
        }),
      ],
    });
  };

  useEffect(() => {
    const isFormValid = updatedService.name && updatedService.applicableState;
    setIsSaveDisabled(!isFormValid);
  }, [updatedService.name, updatedService.applicableState]);

  useEffect(() => {
    setUpdatedService({...service});
  }, [service]);

  useEffect(() => {
    if (
      updatedService.licenseChangesBillingModifier &&
      (updatedService.serviceCredentialModifiers?.length === 0 ||
        !updatedService.serviceCredentialModifiers)
    ) {
      addNewCredential();
    }
  }, [updatedService.licenseChangesBillingModifier]);

  useEffect(() => {
    setDefaultPlaceOfServiceOptions(
      placeOfServices
        .filter((pos: PlaceOfService) =>
          updatedService.placeOfServiceIds?.includes(pos.id),
        )
        .map((pos: PlaceOfService) => ({
          value: pos.id,
          label: pos.name,
        })),
    );
  }, [updatedService.placeOfServiceIds, placeOfServices]);

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const credentialsResponse = await CredentialService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        setCredentials(credentialsResponse.data || []);
        const placeOfServicesResponse = await PlaceOfServiceService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const placeOfServicesData = placeOfServicesResponse.data || [];
        setPlaceOfServices(placeOfServicesData);
        const options = placeOfServicesData.map((pos: PlaceOfService) => ({
          value: pos.id,
          label: pos.name,
        }));
        setPlacesOfServiceOptions(options);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | {target: {id: string; value: any}},
  ) => {
    const {id, value} = e.target;
    setUpdatedService({...updatedService, [id]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    const serviceToSubmit = {
      ...updatedService,
      programId: params.programId || '',
    };
    e.preventDefault();
    if (service?.id) {
      await dispatch(ServicesThunks.update(new Service(serviceToSubmit))).then(
        () => closeDrawer(),
      );
    } else {
      await dispatch(ServicesThunks.create(new Service(serviceToSubmit))).then(
        () => closeDrawer(),
      );
    }
  };

  const handleClose = () => {
    dispatch(setIsEditing(false));
    closeDrawer();
  };

  const handleRadioChange = (field: string, value: boolean) => {
    setUpdatedService((prevState: Service) => ({
      ...prevState,
      [field]: value,
    }));
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {service?.id ? 'Edit Service' : 'Create New Service'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {service?.id ? 'Edit ' : 'Create a new '}service and set its
              parameters
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 px-3 pb-6 pt-3 overflow-y-auto">
          <div className="h-full flex flex-col overflow-y-auto overflow-hidden">
            <div className="h-full px-4">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col space-y-6 items-center mt-4">
                  {isLoading ? (
                    <div className="w-full text-center py-8">
                      <div className="text-gray-500">Loading options...</div>
                    </div>
                  ) : (
                    <>
                      <div className="w-full">
                        <TextInput
                          id="name"
                          type="text"
                          label="Name"
                          value={updatedService.name || ''}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="w-full flex gap-4">
                        <div className="flex flex-col gap-[0.625rem] w-full">
                          <label
                            htmlFor="enableHeader"
                            className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                            Applicable State
                          </label>
                          <CustomSelect
                            options={applicableStateOptions}
                            value={updatedService.applicableState || ''}
                            onChange={selectedOption =>
                              handleChange({
                                target: {
                                  id: 'applicableState',
                                  value: selectedOption.value,
                                },
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-[0.625rem] w-full ">
                        <label
                          htmlFor="enableHeader"
                          className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Places of Service
                        </label>
                        <CustomSelect
                          options={placesOfServiceOptions}
                          isMulti={true}
                          value={updatedService.placeOfServiceIds}
                          onChange={selectedOptions => {
                            const selectedValues = selectedOptions.map(
                              (option: any) => option.value,
                            );
                            setUpdatedService((prevData: Service) => ({
                              ...prevData,
                              placeOfServiceIds: selectedValues,
                            }));
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-[0.625rem] w-full ">
                        <label
                          htmlFor="enableHeader"
                          className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Default PoS
                        </label>
                        <CustomSelect
                          options={defaultPlaceOfServiceOptions}
                          value={updatedService.defaultPlaceOfServiceId || ''}
                          onChange={selectedOption =>
                            handleChange({
                              target: {
                                id: 'defaultPlaceOfServiceId',
                                value: selectedOption.value,
                              },
                            })
                          }
                        />
                      </div>
                      <div className="w-full">
                        <DescriptionInput
                          id="description"
                          type="text"
                          label="Description"
                          value={updatedService.description || ''}
                          placeholder=""
                          onChange={handleChange}
                        />
                      </div>
                      <div className="w-full">
                        <TextInput
                          id="billingCode"
                          type="text"
                          label="Billing Code"
                          value={updatedService.billingCode || ''}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="w-full flex gap-4">
                        <div className="w-1/2">
                          <TextInput
                            id="billingModifier"
                            type="text"
                            label="Billing Modifier"
                            value={updatedService.billingModifier || ''}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="w-1/2">
                          <TextInput
                            id="secondaryModifier"
                            type="text"
                            label="Secondary Modifier"
                            value={updatedService.secondaryModifier || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <RadioInput
                          label="Blocks Time Entry:"
                          options={[
                            {
                              id: 'blocksTimeEntryYes',
                              label: 'Yes',
                              value: true,
                            },
                            {
                              id: 'blocksTimeEntryNo',
                              label: 'No',
                              value: false,
                            },
                          ]}
                          selectedValue={updatedService.blocksTimeEntry}
                          onChange={value =>
                            handleRadioChange('blocksTimeEntry', value)
                          }
                        />
                      </div>
                      <div className="w-full">
                        <RadioInput
                          label="License changes Billing Modifier:"
                          options={[
                            {
                              id: 'licenseChangesBillingModifierYes',
                              label: 'Yes',
                              value: true,
                            },
                            {
                              id: 'licenseChangesBillingModifierNo',
                              label: 'No',
                              value: false,
                            },
                          ]}
                          selectedValue={
                            updatedService.licenseChangesBillingModifier
                          }
                          onChange={value =>
                            handleRadioChange(
                              'licenseChangesBillingModifier',
                              value,
                            )
                          }
                        />
                      </div>
                      {updatedService.licenseChangesBillingModifier && (
                        <div className="w-full">
                          <RadioInput
                            label="License Required for Service:"
                            options={[
                              {
                                id: 'licenseRequiredYes',
                                label: 'Yes',
                                value: true,
                              },
                              {
                                id: 'licenseRequiredNo',
                                label: 'No',
                                value: false,
                              },
                            ]}
                            selectedValue={
                              updatedService.licenseRequiredForService
                            }
                            onChange={value =>
                              handleRadioChange(
                                'licenseRequiredForService',
                                value,
                              )
                            }
                          />
                          <label className="w-full block text-slate-800 text-sm font-semibold dark:text-white mt-6">
                            Credentials:
                          </label>
                          {updatedService.serviceCredentialModifiers?.map(
                            (scm: ServiceCredentialModifier) => {
                              return (
                                <div
                                  key={`${scm.id}-container`}
                                  className="mt-6 grid grid-cols-2 gap-4 w-full">
                                  <div className="flex flex-col w-full">
                                    <CustomSelect
                                      options={credentials
                                        .filter(
                                          credential =>
                                            !updatedService.serviceCredentialModifiers?.some(
                                              (
                                                modifier: ServiceCredentialModifier,
                                              ) =>
                                                modifier.credentialId ===
                                                  credential.id &&
                                                modifier.id !== scm.id,
                                            ),
                                        )
                                        .map(credential => ({
                                          value: credential.id,
                                          label: credential.name,
                                        }))}
                                      onChange={selectedOptions => {
                                        const selectedValue =
                                          selectedOptions.value;
                                        setUpdatedService(
                                          (prevService: Service) => {
                                            const updatedModifiers =
                                              prevService.serviceCredentialModifiers?.map(
                                                (
                                                  modifier: ServiceCredentialModifier,
                                                ) => {
                                                  if (modifier.id === scm.id) {
                                                    return new ServiceCredentialModifier(
                                                      {
                                                        ...modifier,
                                                        credentialId:
                                                          selectedValue,
                                                      },
                                                    );
                                                  }
                                                  return modifier;
                                                },
                                              ) || [];
                                            return {
                                              ...prevService,
                                              serviceCredentialModifiers:
                                                updatedModifiers,
                                            };
                                          },
                                        );
                                      }}
                                      value={scm.credentialId}
                                      menuPlacement="top"
                                    />
                                  </div>
                                  <div className="flex flex-col w-full ">
                                    <TextInput
                                      id={`modifier_${scm.credentialId}`}
                                      type="text"
                                      placeholder="Modifier"
                                      value={scm.modifier || ''}
                                      onChange={e => {
                                        const newModifier = e.target.value;
                                        setUpdatedService(
                                          (prevService: Service) => {
                                            const updatedModifiers =
                                              prevService.serviceCredentialModifiers?.map(
                                                (
                                                  modifier: ServiceCredentialModifier,
                                                ) => {
                                                  if (modifier.id === scm.id) {
                                                    return new ServiceCredentialModifier(
                                                      {
                                                        ...modifier,
                                                        modifier: newModifier,
                                                      },
                                                    );
                                                  }
                                                  return modifier;
                                                },
                                              ) || [];
                                            return {
                                              ...prevService,
                                              serviceCredentialModifiers:
                                                updatedModifiers,
                                            };
                                          },
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            },
                          )}
                          <div className=" mt-6 justify-items-center w-full ">
                            <button
                              type="button"
                              onClick={addNewCredential}
                              className="text-cyan-400 hover:text-cyan-500  text-sm font-medium">
                              + Add Another Modifier
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-5 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label={service.id ? 'Save Service' : 'Create Service'}
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="submit"
              onClick={handleClose}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDrawer;
